import React, { useState } from 'react';
import useStyles from '../styles';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Form from 'components/form';
import Input from 'components/form/Input';
import Select from 'components/form/Select';
import DateInput from 'components/form/DateInput';
import Divider from '@material-ui/core/Divider';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import AddIcon from '@material-ui/icons/Add';
import Button from 'components/common/Button';
import DropZone from 'components/form/Dropzone';
import useSnack from 'hooks/useSnack';
import { useMutation } from '@apollo/react-hooks';
import { UPDATE_STUDENT_PROFILE } from 'graphql/student/Profile';
import extend from 'lodash/extend';
import { Storage } from 'aws-amplify';
import uuidv4 from 'uuidv4';
import moment from 'moment';
import SAT_SUBJECTS from 'constant/sat-subjects';

const BUCKET = process.env.REACT_APP_BUCKET;
const FormItem = Form.FormItem;

const AcademicForm = ({
  highschool_info,
  user,
  disableCard,
  isSkip,
  onSkip,
  saveText,
  form: { getFieldDecorator, validateFields, setFieldsValue },
  step,
  history,
  editable,
}) => {
  const classes = useStyles();
  const snack = useSnack();
  const [update] = useMutation(UPDATE_STUDENT_PROFILE);
  const [satSubjects, setSubjects] = useState(
    highschool_info?.sat_subject_scores || [],
  );
  const [satScores, setSatScores] = useState(highschool_info?.sat_scores || []);
  const [actScores, setActScores] = useState(
    highschool_info?.act_scores || [
      {
        id: uuidv4(),
        math: highschool_info?.act_score_math,
        reading: highschool_info?.act_score_reading,
        english: highschool_info?.act_score_english,
        science: highschool_info?.act_score_science,
      },
    ],
  );

  const onSubmit = () => {
    return validateFields().then((values) => {
      values.sat_subject_scores = satSubjects;
      values.sat_scores = satScores.map((i) => ({
        ...i,
        date: moment(i.date).format('YYYY-MM-DD'),
      }));
      values.act_scores = actScores.map((i) => ({
        ...i,
        date: moment(i.date).format('YYYY-MM-DD'),
      }));
      values = { ...values, completed: true };
      return update({
        variables: {
          id: user.id,
          input: {
            highschool_info: extend(highschool_info, values),
          },
        },
      }).then(
        () => {
          window.analytics.track('Profile: Academic info updated', {
            highschool_info: extend(highschool_info, values),
          });
          snack.success('Profile saved');
          step && history.push(`/onboarding/${step + 1}`);
        },
        (e) => {
          snack.error(e.message);
        },
      );
    });
  };

  const onRemoveFile = (file) => {
    Storage.remove(file.url || file.name);
    setFieldsValue({
      results_image_url: null,
    });
  };

  const handleFileSelect = (files, onProgress, onSuccess, onError) => {
    const file = files[0];
    if (!file) {
      setFieldsValue({
        results_image_url: null,
      });
    } else {
      const key = uuidv4() + '-' + file.name;
      const contentType = file.type;

      Storage.put(key, file, {
        contentType,
        progressCallback: (progress) => {
          const pc = (progress.loaded / progress.total) * 100;
          if (onProgress) {
            onProgress({ percent: pc }, file);
          }
        },
      })
        .then((result) => {
          const url = `https://${BUCKET}.s3.us-east-2.amazonaws.com/public/${result.key}`;
          if (onSuccess) {
            onSuccess(url, file);
          }
          setFieldsValue({
            results_image_url: url,
          });
        })
        .catch((err) => {
          if (onError) {
            onError(err, file);
          }
        });
    }
  };

  const onAddSatScore = () => {
    setSatScores([...satScores, { id: uuidv4() }]);
  };

  const onDeleteSatScore = (i) => {
    satScores.splice(i, 1);
    setSatScores([...satScores]);
  };

  const onSatScoreChange = (item, field) => (value) => {
    item[field] = value;
    setSatScores([...satScores]);
  };

  const onAddSubject = () => {
    setSubjects([...satSubjects, { id: uuidv4() }]);
  };

  const onDeleteSubject = (i) => {
    satSubjects.splice(i, 1);
    setSubjects([...satSubjects]);
  };

  const onSubjectChange = (val, item) => {
    item.subject = val;
    setSubjects([...satSubjects]);
  };

  const onScoreChange = (e, item) => {
    item.score = e.target.value;
    setSubjects([...actScores]);
  };

  const onAddActScore = () => {
    setActScores([...actScores, { id: uuidv4() }]);
  };

  const onDeleteActScore = (i) => {
    actScores.splice(i, 1);
    setActScores([...actScores]);
  };

  const onActScoreChange = (item, field) => (value) => {
    item[field] = value;
    setActScores([...actScores]);
  };

  return (
    <Card className={classes.card} elevation={disableCard && 0}>
      <CardContent className={classes.cardContent}>
        <Typography variant="h5">Academic Info</Typography>
        <Box marginTop={3} width="100%" maxWidth={580}>
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormItem>
                  {getFieldDecorator('current_gpa_weighted', {
                    initialValue: highschool_info?.current_gpa_weighted,
                  })(
                    <Input
                      disabled={!editable}
                      label="Current Weighted GPA"
                      placeholder="Current GPA"
                    />,
                  )}
                </FormItem>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormItem>
                  {getFieldDecorator('current_gpa_unweighted', {
                    initialValue: highschool_info?.current_gpa_unweighted,
                    rules: [{ required: true, message: 'GPA is required' }],
                  })(
                    <Input
                      disabled={!editable}
                      label="Current Unweighted GPA"
                      placeholder="Current GPA"
                    />,
                  )}
                </FormItem>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormItem>
                  {getFieldDecorator('psat_score', {
                    initialValue: highschool_info?.psat_score,
                  })(
                    <Input
                      disabled={!editable}
                      label="PSAT Scores"
                      placeholder="Best PSAT Score or N/A"
                    />,
                  )}
                </FormItem>
              </Grid>
            </Grid>
            <br />
            <Typography
              gutterBottom
              component="p"
              variant="caption"
              color="textPrimary"
            >
              SAT Score
            </Typography>
            {!satScores.length ? (
              <Box marginBottom={2}>
                <Button
                  disabled={!editable}
                  color="primary"
                  variant="outlined"
                  onClick={onAddSatScore}
                  startIcon={<AddIcon />}
                >
                  ADD SAT Score
                </Button>
              </Box>
            ) : (
              <Box marginBottom={3}>
                {satScores.map((i, idx) => (
                  <Grid
                    style={{ paddingBottom: 10 }}
                    container
                    alignItems="flex-end"
                    spacing={1}
                    key={i.id}
                  >
                    <Grid item xs={12} md={4}>
                      <DateInput
                        disabled={!editable}
                        disableMargin
                        fullWidth
                        label="Date"
                        value={i.date}
                        format="L"
                        onChange={onSatScoreChange(i, 'date')}
                        placeholder="Date"
                        inputVariant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Input
                        disabled={!editable}
                        disableMargin
                        label="Overall"
                        value={i.overall}
                        onChange={(e) =>
                          onSatScoreChange(i, 'overall')(e.target.value)
                        }
                        placeholder="Overall"
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Input
                        disabled={!editable}
                        disableMargin
                        value={i.math}
                        label="Math"
                        onChange={(e) =>
                          onSatScoreChange(i, 'math')(e.target.value)
                        }
                        placeholder="Math"
                        type="number"
                      />
                    </Grid>
                    <Grid item md={4}>
                      <Input
                        disabled={!editable}
                        disableMargin
                        label="Reading/Writing"
                        value={i.rw}
                        onChange={(e) =>
                          onSatScoreChange(i, 'rw')(e.target.value)
                        }
                        placeholder="Reading/Writing"
                        type="number"
                      />
                    </Grid>
                    <Grid item sm={3} md={3}>
                      <Button
                        disabled={!editable}
                        className={classes.btnThin}
                        size="large"
                        variant="outlined"
                        color="secondary"
                        onClick={() => onDeleteSatScore(idx)}
                      >
                        <DeleteForeverIcon />
                      </Button>{' '}
                    </Grid>
                    <Grid item sm={3} md={3}>
                      {idx === satScores.length - 1 && (
                        <Button
                          className={classes.btnThin}
                          disabled={!i.date || !i.overall || !i.math || !i.rw || !editable}
                          onClick={onAddSatScore}
                          size="large"
                          variant="contained"
                          color="primary"
                        >
                          Add
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Box>
            )}
            <Box marginBottom={3}>
              <Typography component="p" variant="caption" color="textPrimary">
                SAT Subject Tests
              </Typography>
              <Typography gutterBottom variant="caption">
                At certain colleges, having SAT II scores can impact your
                chances
              </Typography>
            </Box>
            {!satSubjects.length ? (
              <Box marginBottom={2}>
                <Button
                  disabled={!editable}
                  startIcon={<AddIcon />}
                  color="primary"
                  variant="outlined"
                  onClick={onAddSubject}
                >
                  ADD SAT II
                </Button>
              </Box>
            ) : (
              <Box marginBottom={2}>
                {satSubjects.map((i, idx) => (
                  <Grid
                    style={{ paddingBottom: 10 }}
                    container
                    spacing={1}
                    key={i.id}
                  >
                    <Grid item xs={12} md={6}>
                      <Select
                        disabled={!editable}
                        transformValue
                        noMargin
                        label="Please select a subject"
                        className="no-margin"
                        isMulti={false}
                        placeholder="Please select a subject"
                        options={SAT_SUBJECTS}
                        value={i.subject}
                        onChange={(value) => onSubjectChange(value, i)}
                      />
                    </Grid>
                    <Grid item md={3}>
                      <Input
                        disabled={!editable}
                        disableMargin
                        placeholder="Score"
                        label="Score"
                        size="small"
                        type="number"
                        value={i.score}
                        onChange={(e) => onScoreChange(e, i)}
                      />
                    </Grid>
                    <Grid item md={3} style={{ marginTop: 28 }}>
                      <Button
                        disabled={!editable}
                        className={classes.btnThin}
                        size="large"
                        variant="outlined"
                        color="secondary"
                        onClick={() => onDeleteSubject(idx)}
                      >
                        <DeleteForeverIcon />
                      </Button>{' '}
                    </Grid>
                    <Grid item md={3}>
                      {idx === satSubjects.length - 1 && (
                        <Button
                          className={classes.btnThin}
                          disabled={!i.subject || !i.score || !editable}
                          onClick={onAddSubject}
                          size="large"
                          variant="contained"
                          color="primary"
                        >
                          Add
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Box>
            )}
            <Typography
              paragraph
              component="p"
              variant="caption"
              color="textPrimary"
            >
              ACT Score
            </Typography>
            {!actScores.length ? (
              <Box marginBottom={2}>
                <Button
                  disabled={!editable}
                  startIcon={<AddIcon />}
                  color="primary"
                  variant="outlined"
                  onClick={onAddActScore}
                >
                  ADD ACT Score
                </Button>
              </Box>
            ) : (
              <Box marginBottom={4}>
                {actScores.map((i, idx) => (
                  <Grid container spacing={1} key={i.id}>
                    <Grid item xs={12} md={4}>
                      <DateInput
                        disabled={!editable}
                        fullWidth
                        label="Date"
                        value={i.date}
                        format="L"
                        onChange={onSatScoreChange(i, 'date')}
                        placeholder="Date"
                        inputVariant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Input
                        disabled={!editable}
                        label="Overall"
                        disableMargin
                        value={i.overall}
                        onChange={(e) =>
                          onSatScoreChange(i, 'overall')(e.target.value)
                        }
                        placeholder="Overall"
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Input
                        disabled={!editable}
                        label="Math"
                        disableMargin
                        value={i.math}
                        onChange={(e) =>
                          onSatScoreChange(i, 'math')(e.target.value)
                        }
                        placeholder="Math"
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Input
                        disabled={!editable}
                        label="Reading"
                        disableMargin
                        value={i.reading}
                        onChange={(e) =>
                          onSatScoreChange(i, 'reading')(e.target.value)
                        }
                        placeholder="Reading"
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Input
                        disabled={!editable}
                        disableMargin
                        label="English"
                        value={i.english}
                        onChange={(e) =>
                          onSatScoreChange(i, 'english')(e.target.value)
                        }
                        placeholder="English"
                        type="number"
                      />
                    </Grid>
                    <Grid item md={3}>
                      <Input
                        disabled={!editable}
                        disableMargin
                        label="Science"
                        value={i.science}
                        onChange={(e) =>
                          onSatScoreChange(i, 'science')(e.target.value)
                        }
                        placeholder="Science"
                        type="number"
                      />
                    </Grid>
                    <Grid item md={3} style={{ marginTop: 28 }}>
                      <Button
                        disabled={!editable}
                        className={classes.btnThin}
                        size="large"
                        variant="outlined"
                        color="secondary"
                        onClick={() => onDeleteActScore(idx)}
                      >
                        <DeleteForeverIcon />
                      </Button>{' '}
                    </Grid>
                    <Grid item md={3}>
                      {idx === actScores.length - 1 && (
                        <Button
                          className={classes.btnThin}
                          disabled={
                            !i.date ||
                            !i.overall ||
                            !i.math ||
                            !i.reading ||
                            !i.science ||
                            !i.english || !editable
                          }
                          onClick={onAddActScore}
                          size="large"
                          variant="contained"
                          color="primary"
                        >
                          Add
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Box>
            )}
            <FormItem>
              {getFieldDecorator('results_image_url', {
                initialValue: highschool_info?.results_image_url,
              })(
                <DropZone
                  disabled={!editable}
                  accept="image/*,.pdf"
                  listType="card"
                  label="Upload a pdf or screenshot of your latest transcript"
                  onUpload={handleFileSelect}
                  onRemove={onRemoveFile}
                  attachments={
                    highschool_info?.results_image_url
                      ? [{ name: highschool_info?.results_image_url }]
                      : []
                  }
                />,
              )}
            </FormItem>
          </Form>
        </Box>
      </CardContent>
      {!disableCard && <Divider />}
      <CardContent className={classes.footer}>
        <Box display="flex" justifyContent="center">
          {isSkip && (
            <div style={{ marginRight: 24 }}>
              <Button size="large" onClick={onSkip} variant="outlined">
                Skip
              </Button>
            </div>
          )}
          <Button
            size="large"
            variant="contained"
            color="primary"
            onClick={onSubmit}
            disabled={!editable}
          >
            {saveText || 'Save'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default Form.create()(AcademicForm);
